import React from "react"
import Layout from "../../components/layout"
import { withPrefix } from "gatsby"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import Hero from "../../components/hero"
import { PageColumns, Column } from "../../components/page-column"
import { StatContainer, Stat } from "../../components/stat"
import BarChart from "../../components/bar-chart"
import ReadNext from "../../components/read-next"
import Table from "../../components/table"
import GlobalEducationLossesData from "../../data/tables/global-education-losses.json"
import MobileOnly from "../../components/mobile-only"
import MotionIcon from "../../components/motion-icon"
import IconGlobeArrows from "../../images/spotlights/the-great-divergence/icon-globe-arrows.inline.svg"
import Video from "../../components/video"

const PAGESLUG = "the-great-divergence"

const video = (
  <Video
    videoNative={withPrefix("/") + "video/the-great-divergence/the-great-divergence.mp4"}
    track={withPrefix("/") + "video/the-great-divergence/the-great-divergence.vtt"}
    posterImage={"spotlights/the-great-divergence/video-poster.jpg"}
  />
)

const figure1_4 = (
  <BarChart
    id="global-education"
    figureNumber="1.4"
    title="COVID-19 Pandemic Caused Global Learning Loss"
    subTitle="(average missed days of instruction in 2020)"
    caption="Children in the poorest countries lost an average of nearly 70 school days during 2020."
    source="Sources: UNESCO-UNICEF-World Bank Survey on National Education Responses to COVID-19 School Closures; and IMF staff calculations."
  >
    <Table
      data={GlobalEducationLossesData}
      figureNumber="1.4"
      title="COVID-19 Pandemic Caused Global Learning Loss"
      subTitle="(average missed days of instruction in 2020)"
      caption="Children in the poorest countries lost an average of nearly 70 school days during 2020"
      source="Sources: UNESCO-UNICEF-World Bank Survey on National Education Responses to COVID-19 School Closures; and IMF staff calculations."
    />
  </BarChart>
)

const stat = (
  <StatContainer>
    <MotionIcon icon="globe-arrows">
      <IconGlobeArrows />
    </MotionIcon>
    <Stat
      preamble="Low-income countries will need around"
      number={200}
      prefix="$"
      suffix=" billion"
      note="between now and 2025 to respond to the pandemic and a further "
    />
    <Stat
      number={250}
      prefix="$"
      suffix=" billion"
      note="to return to their precrisis convergence path with advanced economies"
      style={{ marginTop: 0 }}
    />
  </StatContainer>
)

export default function TheGreatDivergence() {
  return (
    <Layout>
      <section className="page page--pullBottom page--margin-large">
        <Hero
          title="The Great Divergence"
          text="Without additional efforts to give people a fair shot, cross-country gaps in living standards could widen significantly."
          image="spotlights/the-great-divergence/hero.jpg"
          video={withPrefix("/") + "video/the-great-divergence/hero.mp4?v=1"}
        />

        <PageColumns>
          <Column type="text">
            <p className="dropcap">
              Unprecedented policy responses and rapid progress in vaccine
              development have helped pull the global economy from a deep
              recession, but the economic fallout from the pandemic could be
              with us for years to come—particularly for tourism-dependent
              economies, which suffered some of the biggest losses.
            </p>
            <p>
              Recoveries from the pandemic are diverging dangerously across and
              within countries, with long-lasting scars likely for countries
              with preexisting vulnerabilities and fewer resources for fiscal
              stimulus, vaccine rollouts, and labor force retraining.
              Differences in vaccination rates and fiscal and monetary policy
              responses are driving the divergence. These divergent recovery
              paths are widening the gaps in living standards across countries,
              with nearly half of emerging market and developing economies and
              some middle-income countries now at risk of falling further
              behind.
            </p>
            <MobileOnly>{stat}</MobileOnly>
            <p>
              Divergence within countries is also growing, with youth, women,
              low-skilled workers, and contact-intensive sectors
              disproportionately affected. Because the crisis has accelerated
              the transformative forces of digitalization and automation, some
              of the jobs lost are unlikely to return. Unequal setbacks to
              schooling could further amplify divergent recovery paths.
              Schoolchildren in emerging market and low-income developing
              countries missed more days of instruction in 2020 than children in
              high-income countries (see Figure 1.4). As a result, an estimated
              6 million children are at risk of dropping out of school in 2021,
              with potentially lifelong adverse consequences.
            </p>
          </Column>

          <Column type="media">{stat}</Column>
        </PageColumns>

        <PageColumns modifiers="md:pull--large">
          <Column type="media">
            {figure1_4}
            {video}
          </Column>

          <Column type="text">
            <h2>Support for vulnerable countries </h2>
            <p>
              In the decade leading up to 2019, low-income countries were making
              significant progress toward income convergence with advanced
              economies. COVID-19 dealt low-income countries a major blow in
              this regard, however, pushing an estimated additional 95 million
              people into extreme poverty in 2020 relative to pre-COVID-19
              projections. Ramping up the production and distribution of
              vaccines at affordable prices remains a key priority. But
              significant external support is needed too.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={2} />
            <MobileOnly>{video}</MobileOnly>
            <p>
              The IMF estimates that low-income countries will need about $200
              billion between now and 2025 to respond to the pandemic and a
              further $250 billion to return to their precrisis convergence path
              with advanced economies. A downside scenario of a slower global
              recovery could add $100 billion to these financing needs. Meeting
              these additional needs requires a multifaceted approach.
              Implementing domestic reforms to raise revenues and improve
              governance, spending efficiency, and public financial management
              will be crucial to help resolve the structural lack of adequate
              access to public financing. These reforms should also help foster
              private sector financing, especially for infrastructure.
            </p>
            <MobileOnly>{figure1_4}</MobileOnly>
            <p>
              But this will cover only a portion of low-income countries’
              immediate needs. Grants and concessional loans must bridge the
              gap. The IMF continues to do its part, with lending to low-income
              countries rising to about $12 billion in 2020 and 50 low-income
              countries receiving financial support, largely through emergency
              financing instruments. As countries make the transition to
              multiyear upper-credit-tranche arrangements, the IMF is reviewing
              its lending framework for low-income countries and exploring
              options for scaling up its capacity for concessional lending
              through its Poverty Reduction and Growth Trust (PRGT). Options for
              providing greater support to vulnerable middle-income countries
              are also being considered, with the aim of helping countries to be
              more resilient, green, and inclusive.
            </p>
          </Column>
        </PageColumns>
      </section>

      <ReadNext sectionID="spotlight" pageID="debt-dynamics" />
      <Appendix page={PAGESLUG} />
    </Layout>
  )
}
